import React, { useState } from "react";
import {
  Box,
  Container,
  VStack,
  HStack,
  Select,
  Input,
  Button,
  Text,
  useToast,
  FormControl,
  FormLabel,
  Heading,
  Divider,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from "@chakra-ui/react";
import { supabase } from "../../../lib/supabaseClient";
import * as XLSX from "xlsx";

const Youtube = () => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState("");
  const toast = useToast();

  const [formFields, setFormFields] = useState({
    payout_month: "",
    distributor: "",
    dsp: "youtube",
    data_type: "",
    payout_corp: "",
  });

  // 최근 12개월 생성
  const generateLast12Months = () => {
    const months = [];
    const today = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      months.push(`${year}${month}`);
    }
    return months;
  };

  const handleFileChange = (e) => {
    setSelectedFiles(Array.from(e.target.files));
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setFormFields((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const safeParseFloat = (val) => {
    const cleaned = (val || "").toString().trim();
    if (cleaned === "-" || cleaned === "") return 0;
    const num = parseFloat(cleaned);
    return isNaN(num) ? 0 : num;
  };

  const formatDate = (date) => {
    if (!date) return "";

    // 문자열인 경우 (CSV)
    if (typeof date === "string") {
      return date.slice(0, 6);
    }

    // Date 객체인 경우 (XLSX)
    if (date instanceof Date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      return `${year}${month}`;
    }

    // 숫자인 경우 (XLSX - Excel 날짜)
    if (typeof date === "number") {
      const excelDate = new Date((date - 25569) * 86400 * 1000);
      const year = excelDate.getFullYear();
      const month = String(excelDate.getMonth() + 1).padStart(2, "0");
      return `${year}${month}`;
    }

    return "";
  };

  const groupByISRC = (rows) => {
    const map = new Map();
    for (const row of rows) {
      const key = `${row.sales_month}_${row.isrc}`;
      if (!map.has(key)) {
        map.set(key, {
          payout_month: row.payout_month,
          sales_month: row.sales_month,
          distributor: row.distributor,
          dsp: row.dsp,
          type: row.type,
          payout_corp: row.payout_corp,
          isrc: row.isrc,
          sum_payout: row.sum_payout || 0,
          sum_st_dl: row.sum_st_dl || 0,
        });
      } else {
        const existing = map.get(key);
        existing.sum_payout =
          (existing.sum_payout || 0) + (row.sum_payout || 0);
        existing.sum_st_dl = (existing.sum_st_dl || 0) + (row.sum_st_dl || 0);
      }
    }
    return Array.from(map.values());
  };

  const processFile = async (file, distributor, dataType) => {
    let rows = [];
    const fileExtension = file.name.split(".").pop().toLowerCase();

    if (fileExtension === "csv") {
      const text = await file.text();
      const lines = text.split(/\r?\n/);
      const header = lines[0]
        .split(",")
        .map((h) => h.trim().replace(/^"|"$/g, ""));

      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;

        const cols = line
          .split(/,(?=(?:[^"]*"[^"]*")*[^"]*$)/)
          .map((c) => c.trim().replace(/^"|"$/g, ""));
        const row = {};
        header.forEach((h, idx) => (row[h] = cols[idx] ?? ""));
        rows.push(row);
      }
    } else if (fileExtension === "xlsx") {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];

      // XLSX 파일의 헤더 확인
      const range = XLSX.utils.decode_range(worksheet["!ref"]);
      const headers = [];
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cell = worksheet[XLSX.utils.encode_cell({ r: range.s.r, c: C })];
        headers[C] = cell ? cell.v : undefined;
      }

      // 컬럼 매핑
      const columnMapping = {
        ISRC: "ISRC",
        Day: "Day",
        "Owned Views": "Owned Views",
        "Partner Revenue (KRW)": "Partner Revenue (KRW)",
        원화: "원화",
      };

      // 데이터 변환
      rows = XLSX.utils.sheet_to_json(worksheet, {
        raw: true, // raw 값을 true로 설정하여 원본 값을 가져옴
        dateNF: "yyyy-mm-dd",
        header: headers,
        defval: "",
        range: 1, // 첫 번째 행(헤더)을 제외하고 데이터만 가져오기
      });

      // 컬럼명 정규화 및 ISRC 체크
      rows = rows
        .map((row) => {
          const normalizedRow = {};
          Object.keys(row).forEach((key) => {
            const normalizedKey = columnMapping[key] || key;
            // 숫자 형식의 셀을 일반 텍스트로 변환
            let value = row[key];
            if (typeof value === "number") {
              value = value.toString();
            }
            normalizedRow[normalizedKey] = value;
          });
          return normalizedRow;
        })
        .filter((row) => {
          const isrc = row["ISRC"]?.toString().trim();
          return isrc && isrc !== "-" && isrc !== "" && isrc !== "ISRC"; // "ISRC" 문자열도 제외
        });
    } else {
      throw new Error(
        "지원하지 않는 파일 형식입니다. CSV 또는 XLSX 파일만 업로드 가능합니다."
      );
    }

    const payoutCol =
      distributor === "beyond" ? "원화" : "Partner Revenue (KRW)";
    let rowCount = 0;
    let parsedRows = [];

    for (const row of rows) {
      const isrcRaw = row["ISRC"]?.toString().trim();
      if (!isrcRaw || isrcRaw === "-" || isrcRaw === "" || isrcRaw === "ISRC")
        continue; // "ISRC" 문자열도 제외

      // 통화 형식의 값을 일반 숫자로 변환
      let payoutVal = row[payoutCol];
      if (typeof payoutVal === "string") {
        // 통화 기호와 쉼표 제거
        payoutVal = payoutVal.replace(/[₩,]/g, "");
      }
      payoutVal = safeParseFloat(payoutVal);

      const viewsVal = safeParseFloat(row["Owned Views"]);
      const salesMonth = formatDate(row["Day"]);

      parsedRows.push({
        payout_month: formFields.payout_month,
        sales_month: salesMonth,
        distributor,
        dsp: "youtube",
        type: dataType,
        payout_corp: formFields.payout_corp,
        isrc: isrcRaw,
        sum_payout: payoutVal,
        sum_st_dl: viewsVal,
      });

      rowCount++;
    }

    // ISRC별로 그룹화
    const groupedRows = groupByISRC(parsedRows);

    // 500개씩 나누어 Supabase에 저장
    const batchSize = 500;
    for (let i = 0; i < groupedRows.length; i += batchSize) {
      const batch = groupedRows.slice(i, i + batchSize);
      if (batch.length > 0) {
        await supabase.from("settlement_youtube").upsert(batch);
      }
    }

    // 총 금액 계산
    const totalPayout = groupedRows.reduce(
      (sum, row) => sum + (row.sum_payout || 0),
      0
    );

    return {
      processed: rowCount,
      totalPayout: totalPayout,
    };
  };

  const handleUpload = async (distributor, dataType) => {
    if (
      selectedFiles.length === 0 ||
      !formFields.payout_month ||
      !formFields.payout_corp
    ) {
      toast({
        title: "필수 항목 누락",
        description: "파일, 정산월, 정산법인을 모두 선택해주세요.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    setStatus("업로드 중...");

    let totalProcessed = 0;
    let totalPayout = 0;
    let successCount = 0;
    let failCount = 0;

    try {
      for (const file of selectedFiles) {
        try {
          const result = await processFile(file, distributor, dataType);
          successCount++;
          totalProcessed += result.processed;
          totalPayout += result.totalPayout;

          setUploadedFiles((prev) => [
            {
              fileName: file.name,
              timestamp: new Date().toLocaleString(),
              distributor,
              dataType,
              payoutMonth: formFields.payout_month,
              payoutCorp: formFields.payout_corp,
              processed: result.processed,
              sumPayoutTotal: result.totalPayout,
              status: "성공",
            },
            ...prev,
          ]);

          toast({
            title: "파일 처리 성공",
            description: `${file.name}: ${result.processed}개의 데이터가 처리되었습니다.`,
            status: "success",
            duration: 2000,
            isClosable: true,
          });
        } catch (error) {
          console.error(`Processing error for ${file.name}:`, error);
          failCount++;

          setUploadedFiles((prev) => [
            {
              fileName: file.name,
              timestamp: new Date().toLocaleString(),
              distributor,
              dataType,
              payoutMonth: formFields.payout_month,
              payoutCorp: formFields.payout_corp,
              processed: 0,
              sumPayoutTotal: 0,
              status: "실패",
              error: error.message,
            },
            ...prev,
          ]);

          toast({
            title: "파일 처리 실패",
            description: `${file.name}: ${error.message}`,
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        }
      }

      setStatus(
        `✅ 완료됨. 성공: ${successCount}개, 실패: ${failCount}개, 총 처리 데이터: ${totalProcessed}개, 총 금액: ${totalPayout.toFixed(
          2
        )}원`
      );

      toast({
        title: "전체 처리 완료",
        description: `성공: ${successCount}개, 실패: ${failCount}개, 총 처리 데이터: ${totalProcessed}개, 총 금액: ${totalPayout.toFixed(
          2
        )}원`,
        status: successCount > 0 ? "success" : "error",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Overall processing error:", error);
      setStatus(`❌ 전체 실패: ${error.message}`);
      toast({
        title: "처리 실패",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
      setSelectedFiles([]);
    }
  };

  const calculateTotalByType = (distributor, dataType) => {
    return uploadedFiles
      .filter(
        (file) => file.distributor === distributor && file.dataType === dataType
      )
      .reduce((sum, file) => sum + file.sumPayoutTotal, 0);
  };

  return (
    <Container maxW="container.xl" py={8}>
      <VStack spacing={8} align="stretch">
        <Heading size="lg">유튜브 정산 데이터 업로드</Heading>

        <HStack spacing={4}>
          <FormControl>
            <FormLabel>정산월</FormLabel>
            <Select
              name="payout_month"
              value={formFields.payout_month}
              onChange={handleFieldChange}
              placeholder="정산월 선택"
            >
              {generateLast12Months().map((month) => (
                <option key={month} value={month}>
                  {month.slice(0, 4)}년 {month.slice(4)}월
                </option>
              ))}
            </Select>
          </FormControl>

          <FormControl>
            <FormLabel>정산법인</FormLabel>
            <Select
              name="payout_corp"
              value={formFields.payout_corp}
              onChange={handleFieldChange}
              placeholder="정산법인 선택"
            >
              <option value="루나르트">루나르트</option>
              <option value="수호GRP">수호GRP</option>
            </Select>
          </FormControl>
        </HStack>

        <FormControl>
          <FormLabel>파일</FormLabel>
          <Input
            type="file"
            accept=".csv,.xlsx"
            onChange={handleFileChange}
            multiple
          />
          {selectedFiles.length > 0 && (
            <Text mt={2} fontSize="sm">
              선택된 파일: {selectedFiles.length}개
            </Text>
          )}
        </FormControl>

        <Box>
          <Heading size="md" mb={4}>
            업로드 버튼
          </Heading>
          <HStack spacing={4} wrap="wrap">
            <Button
              colorScheme="blue"
              onClick={() => handleUpload("danal", "ad")}
              isLoading={isLoading}
            >
              youtube-danal-ad
            </Button>
            <Button
              colorScheme="blue"
              onClick={() => handleUpload("danal", "premium")}
              isLoading={isLoading}
            >
              youtube-danal-premium
            </Button>
            <Button
              colorScheme="blue"
              onClick={() => handleUpload("beyond", "ad")}
              isLoading={isLoading}
            >
              youtube-beyond-ad
            </Button>
            <Button
              colorScheme="blue"
              onClick={() => handleUpload("beyond", "premium")}
              isLoading={isLoading}
            >
              youtube-beyond-premium
            </Button>
          </HStack>
        </Box>

        {status && (
          <Box mt={4}>
            <Divider mb={4} />
            <Text fontWeight="bold">상태:</Text>
            <Text>{status}</Text>
          </Box>
        )}

        {uploadedFiles.length > 0 && (
          <Box mt={4}>
            <Divider mb={4} />
            <Heading size="md" mb={4}>
              유형별 총 합계
            </Heading>
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>유통사</Th>
                  <Th>데이터 타입</Th>
                  <Th>총 합계</Th>
                </Tr>
              </Thead>
              <Tbody>
                <Tr>
                  <Td>다날</Td>
                  <Td>광고</Td>
                  <Td>{calculateTotalByType("danal", "ad").toFixed(2)}원</Td>
                </Tr>
                <Tr>
                  <Td>다날</Td>
                  <Td>프리미엄</Td>
                  <Td>
                    {calculateTotalByType("danal", "premium").toFixed(2)}원
                  </Td>
                </Tr>
                <Tr>
                  <Td>비욘드</Td>
                  <Td>광고</Td>
                  <Td>{calculateTotalByType("beyond", "ad").toFixed(2)}원</Td>
                </Tr>
                <Tr>
                  <Td>비욘드</Td>
                  <Td>프리미엄</Td>
                  <Td>
                    {calculateTotalByType("beyond", "premium").toFixed(2)}원
                  </Td>
                </Tr>
                <Tr fontWeight="bold">
                  <Td colSpan={2}>전체 합계</Td>
                  <Td>
                    {uploadedFiles
                      .reduce((sum, file) => sum + file.sumPayoutTotal, 0)
                      .toFixed(2)}
                    원
                  </Td>
                </Tr>
              </Tbody>
            </Table>
          </Box>
        )}

        {uploadedFiles.length > 0 && (
          <Box mt={4}>
            <Divider mb={4} />
            <Heading size="md" mb={4}>
              업로드된 파일 목록
            </Heading>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>파일명</Th>
                  <Th>업로드 시간</Th>
                  <Th>유통사</Th>
                  <Th>데이터 타입</Th>
                  <Th>정산월</Th>
                  <Th>정산법인</Th>
                  <Th>처리 건수</Th>
                  <Th>총 금액</Th>
                  <Th>상태</Th>
                </Tr>
              </Thead>
              <Tbody>
                {uploadedFiles.map((file, index) => (
                  <Tr
                    key={index}
                    bg={file.status === "실패" ? "red.50" : undefined}
                  >
                    <Td>{file.fileName}</Td>
                    <Td>{file.timestamp}</Td>
                    <Td>{file.distributor}</Td>
                    <Td>{file.dataType}</Td>
                    <Td>{file.payoutMonth}</Td>
                    <Td>{file.payoutCorp}</Td>
                    <Td>{file.processed}</Td>
                    <Td>{file.sumPayoutTotal.toFixed(2)}원</Td>
                    <Td
                      color={file.status === "실패" ? "red.500" : "green.500"}
                    >
                      {file.status}
                      {file.error && (
                        <Text fontSize="xs" color="red.500">
                          {file.error}
                        </Text>
                      )}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}
      </VStack>
    </Container>
  );
};

export default Youtube;
